import { Fragment } from "react";
import {
  ArrowDown,
  ArrowDownUp,
  ArrowUp,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDelayedFlag } from "@/hooks";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import PaginationControls from "./PaginationControls";

const SortableHeader = ({
  label,
  sortKey,
  sortState,
  onSort,
  showSortIndicators = false,
  align,
  width,
  className,
}) => {
  const isSorted = sortState?.key === sortKey;
  const isDescending =
    isSorted &&
    (sortState?.direction === "desc" || sortState?.direction === "descending");
  const ariaSort = isSorted
    ? isDescending
      ? "descending"
      : "ascending"
    : "none";

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSort?.(sortKey);
    }
  };

  return (
    <TableHead
      aria-sort={ariaSort}
      style={width ? { width } : undefined}
      className={cn(
        "cursor-pointer select-none text-primary",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
      onClick={() => onSort?.(sortKey)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
    >
      <div
        className={cn(
          "flex items-center gap-1",
          align === "right" && "justify-end",
          align === "center" && "justify-center",
        )}
      >
        <span>{label}</span>
        {showSortIndicators ? (
          isSorted ? (
            isDescending ? (
              <ChevronDown className="h-3 w-3 text-primary" />
            ) : (
              <ChevronUp className="h-3 w-3 text-primary" />
            )
          ) : (
            <ArrowDownUp className="h-3 w-3 text-muted-foreground/60" />
          )
        ) : (
          isSorted && (
            <span className="inline-flex items-center">
              {isDescending ? (
                <ArrowDown className="h-3 w-3" />
              ) : (
                <ArrowUp className="h-3 w-3" />
              )}
            </span>
          )
        )}
      </div>
    </TableHead>
  );
};

const renderFilterControl = (filter) => {
  if (!filter) return null;
  if (filter.render !== undefined) {
    return typeof filter.render === "function"
      ? filter.render()
      : filter.render;
  }

  if (filter.type === "text") {
    return (
      <Input
        value={filter.value ?? ""}
        onChange={(event) => filter.onChange?.(event.target.value)}
        placeholder={filter.placeholder}
        className={cn("w-full", filter.className)}
      />
    );
  }

  return null;
};

const DataTable = ({
  columns,
  data,
  loading = false,
  emptyMessage = "No data found.",
  loadingMessage = "Loading...",
  sort,
  onSort,
  filtersEnabled = true,
  getRowKey = (row, index) => row?.id ?? index,
  getRowProps,
  pagination,
  minLoadingMs,
  loadingDelay,
  renderExpansion,
}) => {
  const visibleColumns = columns.filter((column) => column && !column.hidden);
  const columnCount = visibleColumns.length;
  const hasFilters =
    filtersEnabled && visibleColumns.some((column) => column.filter);
  const resolvedMinDuration =
    typeof minLoadingMs === "number" ? minLoadingMs : loadingDelay;
  const showSkeleton = useDelayedFlag(loading, resolvedMinDuration);
  const isLoading = loading || showSkeleton;
  const skeletonRows = Math.min(pagination?.pageSize ?? 20, 20);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="min-w-0 overflow-hidden rounded-radius border border-border bg-card/40">
        <Table className="min-w-full">
          <TableHeader>
            <TableRow>
              {visibleColumns.map((column) => {
                const align = column.align;
                const headerKey = column.key;
                const commonProps = {
                  width: column.width,
                  className: column.headerClassName,
                };

                if (column.sortable && onSort) {
                  const sortKey = column.sortKey || column.key;
                  return (
                    <SortableHeader
                      key={headerKey}
                      {...commonProps}
                      label={column.header}
                      sortKey={sortKey}
                      sortState={sort}
                      onSort={onSort}
                      showSortIndicators={column.showSortIndicators}
                      align={align}
                    />
                  );
                }

                return (
                  <TableHead
                    key={headerKey}
                    {...commonProps}
                    style={column.width ? { width: column.width } : undefined}
                    className={cn(
                      "text-primary",
                      align === "right" && "text-right",
                      align === "center" && "text-center",
                      column.headerClassName,
                    )}
                  >
                    {column.header}
                  </TableHead>
                );
              })}
            </TableRow>
            {hasFilters && (
              <TableRow className="bg-muted/30">
                {visibleColumns.map((column) => (
                  <TableHead
                    key={`${column.key}-filter`}
                    className={cn(
                      "py-3 font-normal",
                      column.align === "right" && "text-right",
                      column.align === "center" && "text-center",
                    )}
                  >
                    {renderFilterControl(column.filter)}
                  </TableHead>
                ))}
              </TableRow>
            )}
          </TableHeader>
          <TableBody
            aria-busy={isLoading ? "true" : undefined}
            aria-live="polite"
            aria-label={isLoading ? loadingMessage : undefined}
          >
            {isLoading ? (
              Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={`${column.key}-skeleton-${rowIndex}`}
                      className={cn(
                        column.align === "right" && "text-right",
                        column.align === "center" && "text-center",
                        column.cellClassName,
                      )}
                    >
                      <Skeleton
                        className={cn(
                          "h-4",
                          column.align === "right" && "ml-auto",
                          column.align === "center" && "mx-auto",
                        )}
                        style={
                          column.width
                            ? { width: column.width }
                            : { width: "100%", maxWidth: "220px" }
                        }
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
                const key = getRowKey(row, index);
                const rowProps = getRowProps?.(row) ?? {};
                const expansion = renderExpansion?.(row, index);

                return (
                  <Fragment key={key}>
                    <TableRow {...rowProps}>
                      {visibleColumns.map((column) => {
                        const align = column.align;
                        const cellContent = column.render
                          ? column.render(row, index)
                          : row?.[column.accessor || column.key];

                        return (
                          <TableCell
                            key={`${key}-${column.key}`}
                            className={cn(
                              align === "right" && "text-right",
                              align === "center" && "text-center",
                              column.cellClassName,
                            )}
                            style={
                              column.width
                                ? {
                                    width: column.width,
                                    maxWidth: column.width,
                                  }
                                : undefined
                            }
                          >
                            {cellContent}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {expansion && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={columnCount} className="p-0">
                          {expansion}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && (
        <PaginationControls
          {...pagination}
          disabled={pagination.disabled ?? loading}
        />
      )}
    </div>
  );
};

export { DataTable, SortableHeader };
